<div class="bg-white rounded-lg shadow-md p-6">
  <h2 class="text-xl font-bold mb-4">
    {{ isEditMode ? 'Edit' : 'Create' }} Promotion {{ size().width }}x{{ size().height }}
  </h2>
  
  <form [formGroup]="promotionForm" (ngSubmit)="onSave()" class="space-y-4">
    <!-- Discount Price and Percentage Row -->
    <div class="grid grid-cols-2 gap-4">
      <!-- Discount Price -->
      <div>
        <label for="discountPrice" class="block text-sm font-medium text-gray-700 mb-2">
          Discount Price
        </label>
        <div class="relative">
          <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
          <input
            id="discountPrice"
            type="number"
            step="0.01"
            min="0"
            formControlName="discountPrice"
            placeholder="0.00"
            class="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            [class.border-red-500]="promotionForm.get('discountPrice')?.invalid && promotionForm.get('discountPrice')?.touched"
          />
        </div>
      </div>

      <!-- Discount Percentage -->
      <div>
        <label for="discountPercentage" class="block text-sm font-medium text-gray-700 mb-2">
          Discount
        </label>
        <div class="relative">
          <input
            id="discountPercentage"
            type="number"
            min="0"
            max="100"
            formControlName="discountPercentage"
            placeholder="0"
            class="w-full pr-8 pl-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            [class.border-red-500]="promotionForm.get('discountPercentage')?.invalid && promotionForm.get('discountPercentage')?.touched"
          />
          <span class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
        </div>
      </div>
    </div>

    <!-- Start Date -->
    <div>
      <label for="startDate" class="block text-sm font-medium text-gray-700 mb-2">
        Start Date
      </label>
      <input
        id="startDate"
        type="date"
        formControlName="startDate"
        [min]="todayString"
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        [class.border-red-500]="promotionForm.get('startDate')?.invalid && promotionForm.get('startDate')?.touched"
      />
      @if (startDateErrors) {
        <p class="mt-1 text-sm text-red-600">{{ startDateErrors }}</p>
      }
    </div>

    <!-- End Date -->
    <div>
      <label for="endDate" class="block text-sm font-medium text-gray-700 mb-2">
        End Date
      </label>
      <input
        id="endDate"
        type="date"
        formControlName="endDate"
        [min]="promotionForm.get('startDate')?.value || todayString"
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        [class.border-red-500]="promotionForm.get('endDate')?.invalid && promotionForm.get('endDate')?.touched"
      />
      @if (endDateErrors) {
        <p class="mt-1 text-sm text-red-600">{{ endDateErrors }}</p>
      }
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-between items-center pt-4">
      <!-- Delete Button (only in edit mode) -->
      @if (isEditMode) {
        <button
          type="button"
          (click)="onDeleteClick()"
          [disabled]="isLoading()"
          class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
          </svg>
          Delete
        </button>
      } @else {
        <div></div>
      }

      <!-- Save/Update Button -->
      <button
        type="submit"
        [disabled]="promotionForm.invalid || isLoading()"
        class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
      >
        @if (isLoading()) {
          <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ isEditMode ? 'Updating...' : 'Saving...' }}
        } @else {
          {{ isEditMode ? 'Update' : 'Save' }} Promotion
        }
      </button>
    </div>

    <!-- Error Message -->
    @if (errorMessage()) {
      <div class="mt-4 p-3 rounded-md bg-red-50 border border-red-200">
        <p class="text-sm font-medium text-red-800">{{ errorMessage() }}</p>
      </div>
    }
  </form>

  <!-- Delete Confirmation Dialog -->
  @if (showDeleteConfirmation()) {
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/50" (click)="onDeleteCancel()"></div>
      
      <!-- Modal content -->
      <div class="relative bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
        <div class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          
          <h3 class="text-lg font-medium text-gray-900 mb-2">Delete Promotion</h3>
          <p class="text-sm text-gray-500 mb-6">
            Are you sure you want to delete this promotion for {{ size().width }}×{{ size().height }}cm? This action cannot be undone.
          </p>
          
          <div class="flex gap-3 justify-center">
            <button
              type="button"
              (click)="onDeleteCancel()"
              class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              (click)="onDeleteConfirm()"
              class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  }
</div>
